import { ReactNode } from 'react';

import { Box, Stack } from '@mui/material';

import {
  COMPACT_BADGES_SX,
  COMPACT_HEAD_SX,
  COMPACT_HEAD_WRAP_SX,
  COMPACT_LINE_ONE_SX,
  TITLE_ACTIONS_SX,
  WIDE_ROW
} from './listRowFrameStyles';

/**
 * Порог головы `compact` — контейнерный запрос домена (`buildRowQuery`): уже
 * него `meta` домена стоит на своей второй строке во всю ширину (домен сам даёт
 * ей `flexBasis: '100%'` под тем же порогом), от него голова — одна строка без
 * переноса. Так устроены кастинг (560) и проект (440). Без порога `meta` всегда
 * стоит рядом с заголовком (визитка, актёр)
 */
export type TCompactHeadWrap = string;

interface IProps {
  title: ReactNode;
  titleActions?: ReactNode;
  meta?: ReactNode;
  compactHeadWrap?: TCompactHeadWrap;
  badges?: ReactNode;
}

/**
 * Состояние «одна строка» первой строки головы: обёртка первой строки
 * растворяется (`display: contents`), её дети становятся прямыми флекс-элементами
 * головы, а резерв угла уходит в конец ряда (`order: 1`) — после `meta` и
 * плашки, стоящих в той же строке. Голова при этом не переносится
 * @param query - контейнерный запрос, от которого действует состояние
 */
const buildOneLineSx = (query: string) => ({
  [query]: {
    flexWrap: 'nowrap',
    '& > [data-row-line-one]': { display: 'contents' },
    '& [data-row-reserve]': { order: 1 }
  }
});

/**
 * Первая строка головы: заголовок [+ `meta` домена без порога] + резерв угла,
 * неразрывно (`COMPACT_LINE_ONE_SX`). Без резерва обёртка не нужна — заголовок
 * и `meta` идут прямыми флекс-элементами головы
 * @param props - пропсы
 * @param props.title - заголовок строки
 * @param props.meta - `meta`, если она стоит в первой строке
 * @param props.reserve - резерв/действия угла трейлинга
 */
const LineOne = ({
  title,
  meta,
  reserve
}: {
  title: ReactNode;
  meta?: ReactNode;
  reserve?: ReactNode;
}) =>
  reserve ? (
    <Box data-row-line-one sx={COMPACT_LINE_ONE_SX}>
      {title}
      {meta}
      <Box data-row-reserve sx={TITLE_ACTIONS_SX}>
        {reserve}
      </Box>
    </Box>
  ) : (
    <>
      {title}
      {meta}
    </>
  );

/**
 * Голова `compact`: заголовок, `meta` и плашка статуса. Вынесена из
 * `ListRowFrame` (лимит длины файла каркаса).
 *
 * Резерв угла трейлинга (`titleActions`) стоит ТОЛЬКО на первой строке — там,
 * где каркас рисует кебаб/иконки (`getCornerSx`, `top`). Первая строка — своя
 * неразрывная обёртка (`data-row-line-one`): заголовок [+ `meta` у домена без
 * порога] + резерв; всё, что уходит на вторую строку (`meta` домена с порогом,
 * плашка уже `WIDE_ROW`), идёт во всю ширину головы. На широкой строке обёртка
 * растворяется (`buildOneLineSx`), и резерв встаёт в конец ряда — после
 * плашки, поэтому плашка не заезжает под кебаб. Волна 4 19243 ставила резерв
 * соседом всей головы, и вторая строка теряла его ширину (роль кастинга на 375
 * сужалась на 88px)
 * @param props - пропсы
 * @param props.title - заголовок строки
 * @param props.titleActions - резерв/действия угла трейлинга
 * @param props.meta - готовая строка метаданных домена
 * @param props.compactHeadWrap - перенос головы (см. `TCompactHeadWrap`)
 * @param props.badges - плашка статуса
 */
export const CompactRowHead = ({
  title,
  titleActions,
  meta,
  compactHeadWrap,
  badges
}: IProps) => {
  // `meta` домена с порогом уходит на вторую строку — в первую строку её не
  // кладём; у домена без порога `meta` всегда рядом с заголовком
  const lineOneMeta = compactHeadWrap ? undefined : meta;
  // Без порога домена первая строка растворяется там же, где плашка встаёт в
  // строку (`WIDE_ROW`): резерв должен оказаться после неё
  const lineOneQuery = compactHeadWrap || (badges ? WIDE_ROW : undefined);

  // `overflow: hidden` (`COMPACT_HEAD_SX`) — не косметика: заголовок и `meta`
  // делят одну строку, домен защищает от сжатия «неделимые» термы своей `meta`
  // (дата/число, `flexShrink: 0`), и при их совместной ширине больше доступной
  // эти термы не сжимаются, а бесконтрольно выходят за границы строки — на
  // телефоне вплоть до правого края карточки (18976, дефект «дата вылезает за
  // карточку»). Каркас — единственное место, которое видит оба слота разом,
  // поэтому контейнмент — его забота, а не домена.
  return (
    <Stack
      data-testid='list-row-head'
      direction='row'
      alignItems='center'
      gap={2}
      sx={[
        // `flexWrap: 'nowrap'` — явный CSS-инициал (см.
        // `COMPACT_HEAD_WRAP_SX`), а не перестраховка: без порога домена
        // (и без плашки статуса) голова обязана остаться нерастущей по
        // высоте, как раньше
        COMPACT_HEAD_SX,
        (!!compactHeadWrap || !!badges) && COMPACT_HEAD_WRAP_SX,
        !!lineOneQuery && buildOneLineSx(lineOneQuery)
      ]}
    >
      <LineOne title={title} meta={lineOneMeta} reserve={titleActions} />
      {!!compactHeadWrap && meta}
      {!!badges && <Box sx={COMPACT_BADGES_SX}>{badges}</Box>}
    </Stack>
  );
};
